import { Equal, AlertTriangle, Hourglass } from 'lucide-react';
import { cn } from '@/lib/utils';
import { stabilityState, type StabilityState } from '@/lib/statistics';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';

interface StabilityChipProps {
  delta7: number | null;
  delta14: number | null;
  delta28: number | null;
}

function fmt(d: number | null): string {
  if (d === null) return '—';
  const sign = d > 0 ? '+' : '';
  return `${sign}${d.toFixed(0)}%`;
}

/**
 * Vorzeichen der gemessenen Fenster, sofern es EINDEUTIG ist.
 *
 * `stabilityState` kennt nur die SPANNE (`max - min <= 15pp`) und damit nur
 * Konsistenz, nie Richtung: (-40, -45, -42) ist genauso `stable` wie
 * (40, 45, 42). Eine Farbe, die am Zustand haengt, behauptet deshalb
 * zwangslaeufig etwas, das der Zustand nicht weiss — genau das war der Befund:
 * gruenes Pill mit Aufwaertspfeil ueber "-40% / -45% / -42%", buchstabengleich
 * zum Fall (40, 45, 42), zwei Zellen neben derselben Zahl in Rot.
 *
 * `mixed` ist kein Randfall, sondern erreichbar: (-5, +5, +2) hat eine Spanne
 * von 10pp, ist also `stable`, ohne dass sich eine Richtung sagen liesse. Dann
 * bleibt die Farbe neutral, statt sich eine auszusuchen.
 */
type Direction = 'up' | 'down' | 'mixed';

function consistentDirection(deltas: readonly number[]): Direction {
  if (deltas.length === 0) return 'mixed';
  if (deltas.every((d) => d > 0)) return 'up';
  if (deltas.every((d) => d < 0)) return 'down';
  return 'mixed';
}

/**
 * Farbe folgt dem Vorzeichen, nicht dem Zustand.
 *
 * Das ist die Regel, die auf dieser Flaeche ueberall sonst schon gilt:
 * `liftColor` in `PerformanceSigKpiCards`, `liftColorClass` in `ExperimentCard`
 * und die Aenderungs-Spalte der Vergleichstabelle leiten ihre Farbe alle aus
 * dem Vorzeichen ab. Damit kann die Stabilitaets-Zelle der Aenderungs-Zelle
 * derselben Zeile nicht mehr widersprechen.
 *
 * Die konservative Alternative waere ein durchgaengig neutrales Pill gewesen
 * (Konsistenz ist kein Urteil). Verworfen, weil sie Information wegwirft, die
 * der Nutzer an genau dieser Stelle braucht: ein Rueckgang, der ueber alle drei
 * Fenster haelt, ist dringender als ein schwankender, und die Spalte existiert,
 * um die Frage "haelt die Aenderung?" zu beantworten. Neutral haette sie nur
 * noch "ja" geantwortet, ohne zu sagen, was da haelt.
 *
 * BEIDE SEITEN TRAGEN DAS `-ink`-TOKEN, nicht das Flaechen-Token.
 *
 * `--destructive` ist als SCHRIFT auf `bg-destructive/10` nachgerechnet zu
 * schwach: 3.31:1 ueber `--card`, 3.16:1 ueber `--background` (Rechner
 * `src/test/contrast.ts`, festgenagelt in `src/lib/contrast.test.ts`). Der Text
 * ist hier 10 px, also normaler Text, und AA verlangt 4.5:1. Mit
 * `--destructive-ink` sind es 5.62:1 bzw. 5.36:1.
 *
 * Die gruene Seite eine Zeile darueber war seit Welle 11 schon richtig
 * (`--success-ink`, 5.59:1) — durchgefallen ist nur die rote, und im Dark Mode
 * ist der Defekt unsichtbar, weil `--destructive-ink` dort mit `--destructive`
 * identisch ist. Eine Farbregel, die nur in einem Theme haelt, faellt genau
 * dann auf, wenn ein Nutzer sie braucht.
 */
const STABLE_CLASSES: Record<Direction, string> = {
  up: 'bg-success/10 text-success-ink border-success/30',
  down: 'bg-destructive/10 text-destructive-ink border-destructive/30',
  mixed: 'bg-muted text-muted-foreground border-border',
};

/**
 * Das eine Fenster, das noch fehlt — oder `null`, wenn alle drei gemessen sind.
 *
 * `stabilityState` erklaert einen Chip schon ab ZWEI Fenstern als `stable`
 * (`lib/statistics.ts`: `deltas.length < 2` ist `incomplete`). Der Hinweis
 * darunter darf deshalb nicht "ueber alle Fenster" behaupten, waehrend derselbe
 * Tooltip zwei Zeilen hoeher "28d: —" auflistet.
 *
 * Bewusst der KONKRETE Name statt einer festen "28-Tage"-Formulierung: welches
 * Fenster fehlt, steht hier fest und ist nicht immer das laengste. Die Fenster
 * sind feste Nach-Upload-Fenster, und jedes einzelne wird von
 * `get_optimization_stability` genullt, sobald es unter 100 Impressionen oder
 * 5 Klicks bleibt — ein Feed mit schwachem Start kann also 7d verlieren und
 * 14d/28d behalten. Eine hart getextete "28d fehlt"-Aussage waere in genau dem
 * Fall falsch.
 */
function missingWindowLabel(delta7: number | null, delta14: number | null, delta28: number | null): string | null {
  const missing = ([[delta7, '7d'], [delta14, '14d'], [delta28, '28d']] as const)
    .filter(([v]) => v === null)
    .map(([, label]) => label);
  return missing.length === 1 ? missing[0] : null;
}

export function StabilityChip({ delta7, delta14, delta28 }: StabilityChipProps) {
  const { t } = useTranslation('performance');
  const state: StabilityState = stabilityState(delta7, delta14, delta28);
  const deltas = [delta7, delta14, delta28].filter((v): v is number => v !== null);
  const missingWindow = missingWindowLabel(delta7, delta14, delta28);
  /**
   * Gemessen, aber nichts bewegt: jedes vorhandene Fenster steht exakt auf 0.
   *
   * Der Fall ist `stable` (Spanne 0) und `mixed` (kein Delta ist `> 0` oder
   * `< 0`), faellt also in den neutralen Zweig — und traegt damit den Hinweis
   * "Effekt bleibt konsistent" ueber einer Auflistung aus drei Nullen. Der Satz
   * behauptet keine RICHTUNG, aber einen Effekt, und den gibt es hier nicht.
   */
  const noEffect = deltas.length > 0 && deltas.every((d) => d === 0);

  const config = {
    stable: {
      // `Equal` statt `TrendingUp`. "Stabil" heisst, dass die drei Fenster
      // UNTEREINANDER uebereinstimmen — zwei parallele Striche sagen genau das
      // und behaupten keine Richtung.
      //
      // `ArrowUpDown` waere hier falsch: der Doppelpfeil liest sich als
      // "schwankend", also als das Gegenteil des Zustands, den er beschriftet
      // (in `PerformanceSigKpiCards` ist er richtig, weil dort eine
      // zweiseitige MENGE gezaehlt wird). `Minus` ist bereits belegt: in der
      // Aenderungs-Spalte derselben Zeile steht es fuer "keine Aenderung", und
      // dasselbe Zeichen darf zwei Zellen weiter nicht "konsistent" heissen.
      icon: Equal,
      classes: STABLE_CLASSES[consistentDirection(deltas)],
      label: t('stability.stable', 'stable'),
    },
    volatile: {
      // Bernstein bleibt. "Volatil" heisst, dass sich aus den Fenstern KEINE
      // Aussage ableiten laesst, und Vorsicht ist dafuer das richtige Signal.
      // Eine vorzeichenabhaengige Farbe waere hier sogar falsch: sie wuerde
      // eine Richtung anbieten, wo die Messung keine hergibt. `AlertTriangle`
      // behauptet keine Richtung, sondern den eigenen, wahren Befund des Chips.
      icon: AlertTriangle,
      classes: 'bg-warning/10 text-warning-ink border-warning/30',
      label: t('stability.volatile', 'volatile'),
    },
    incomplete: {
      // Grau + Sanduhr: noch keine Messung, also auch kein Urteil.
      icon: Hourglass,
      classes: 'bg-muted text-muted-foreground border-border',
      label: t('stability.tracking', 'tracking'),
    },
  } as const;
  const { icon: Icon, classes, label } = config[state];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono border', classes)}>
          <Icon className="h-3 w-3" />
          <span>{fmt(delta7)} / {fmt(delta14)} / {fmt(delta28)}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="text-xs space-y-1">
          <div className="font-semibold">{label}</div>
          <div>7d: {fmt(delta7)}</div>
          <div>14d: {fmt(delta14)}</div>
          <div>28d: {fmt(delta28)}</div>
          {state === 'stable' && (
            // Richtungsfrei ist der Hinweis schon seit Welle 11: beide tragenden
            // Woerter ("Effekt", "konsistent") sind vorzeichenlos, der Satz
            // stimmt also auch bei -40/-45/-42. Die Richtung liefern die drei
            // vorzeichenbehafteten Zahlen direkt darueber plus die Farbe.
            //
            // Was er zu VIEL sagte, war die MENGE: "ueber alle Fenster", waehrend
            // zwei Zeilen hoeher "28d: —" steht. Zwei gemessene Fenster genuegen
            // dem Zustand, also sagt der Hinweis in diesem Fall genau das und
            // benennt das fehlende.
            //
            // Dritter Zweig fuer den Null-Effekt (0/0/0): dort ist "Effekt
            // konsistent" die letzte verbliebene Ueberbehauptung dieses
            // Tooltips. Unterdruecken waere kuerzer, wirft aber die Aussage weg,
            // die der Nutzer hier tatsaechlich braucht — gemessen UND nichts
            // passiert ist ein Befund, kein fehlender Wert, und ohne Hinweis
            // liesse sich das nicht von "noch keine Daten" unterscheiden.
            //
            // Die Reihenfolge gibt `missingWindow` den Vortritt: bei (0, 0, null)
            // ist "beide gemessenen Fenster stimmen ueberein" wahr und behauptet
            // ebenfalls keinen Effekt, der Zweig braucht also keine eigene
            // Null-Variante.
            <div className="text-muted-foreground">
              {missingWindow
                ? t('stability.stableHintPartial', {
                    defaultValue: 'The two measured windows agree; the {{missing}} window is still missing',
                    missing: missingWindow,
                  })
                : noEffect
                  ? t('stability.stableHintNoEffect', 'All three windows show no change')
                  : t('stability.stableHint', 'Effect consistent across windows')}
            </div>
          )}
          {state === 'volatile' && (
            <div className="text-muted-foreground">
              {t('stability.volatileHint', 'Effect varies across windows, so interpret with caution')}
            </div>
          )}
          {state === 'incomplete' && (
            <div className="text-muted-foreground">
              {t('stability.incompleteHint', 'Not enough post-data yet for cross-window comparison')}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
